// client/src/pages/PTO/FrontDeskCoverage.tsx
// Weekly grid showing front desk coverage assignments by office.

import React, { useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import type {
  FrontDeskAssignment,
  FrontDeskDefault,
  PTOEmployee,
} from './ptoTypes';
import { weekDatesFor, formatTime12, getOfficeColor } from './ptoUtils';
import CoverageEditModal from './CoverageEditModal';

// ─── Props ──────────────────────────────────────────────────────────────────

interface FrontDeskCoverageProps {
  frontDesk: FrontDeskAssignment[];
  frontDeskDefaults: FrontDeskDefault[];
  employees: PTOEmployee[];
  currentWeek: Date;
  canEdit: boolean;
  onSaveFrontDesk?: (payload: {
    id?: number;
    office: string;
    coverageDate: string;
    staffIndex: string;
    timeIn: string | null;
    timeOut: string | null;
    note: string | null;
  }) => Promise<void>;
  onDeleteFrontDesk?: (id: number) => Promise<void>;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const FRONT_DESK_OFFICES = ['Birmingham', 'Huntsville', 'Mobile', 'Gadsden', 'Ridgeland'];
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// ─── Helpers ────────────────────────────────────────────────────────────────

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Format ISO date as short label: "Mon 4/13". */
function dayHeader(iso: string, idx: number): string {
  const [, m, d] = iso.split('-');
  return `${DAY_LABELS[idx]} ${Number(m)}/${Number(d)}`;
}

/** Get day of week (0=Mon through 4=Fri) from ISO date. Matches DB convention. */
function isoDayOfWeek(iso: string): number {
  const [y, m, d] = iso.split('-');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const dow = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  return dow === 0 ? 6 : dow - 1; // 0=Mon, 1=Tue, ..., 4=Fri, 5=Sat, 6=Sun
}

interface CoverageEntry {
  employeeName: string;
  timeRange: string;
}

interface CoverageCell {
  entries: CoverageEntry[];   // one or more people covering the day
  isNoCoverage: boolean;
  isOverride: boolean;        // true = day-specific overrides; false = standing defaults
}

// ─── Component ──────────────────────────────────────────────────────────────

const FrontDeskCoverage: React.FC<FrontDeskCoverageProps> = ({
  frontDesk,
  frontDeskDefaults,
  employees,
  currentWeek,
  canEdit,
  onSaveFrontDesk,
  onDeleteFrontDesk,
}) => {
  const weekDates = useMemo(() => weekDatesFor(currentWeek), [currentWeek]);
  const today = todayIso();
  const [editTarget, setEditTarget] = useState<{ office: string; date: string } | null>(null);

  const empMap = useMemo(() => {
    const m = new Map<string, PTOEmployee>();
    for (const e of employees) m.set(e.staffIndex, e);
    return m;
  }, [employees]);

  // Build override lookup: office+date -> sorted FrontDeskAssignment[]
  // (a day can have several rows, including the same person more than once).
  const overrideMap = useMemo(() => {
    const m = new Map<string, FrontDeskAssignment[]>();
    for (const fd of frontDesk) {
      const key = `${fd.office}|${fd.coverageDate}`;
      const list = m.get(key) ?? [];
      list.push(fd);
      m.set(key, list);
    }
    // Sort each day's slots by timeIn, then by employee sort order.
    for (const list of m.values()) {
      list.sort((a, b) => {
        const aTime = a.timeIn ?? '00:00';
        const bTime = b.timeIn ?? '00:00';
        if (aTime !== bTime) return aTime.localeCompare(bTime);
        const ea = empMap.get(a.staffIndex);
        const eb = empMap.get(b.staffIndex);
        return (ea?.sortOrder ?? 0) - (eb?.sortOrder ?? 0);
      });
    }
    return m;
  }, [frontDesk, empMap]);

  // Build default lookup: office+dayOfWeek -> FrontDeskDefault[]
  const defaultMap = useMemo(() => {
    const m = new Map<string, FrontDeskDefault[]>();
    for (const fd of frontDeskDefaults) {
      const key = `${fd.office}|${fd.dayOfWeek}`;
      const list = m.get(key) ?? [];
      list.push(fd);
      m.set(key, list);
    }
    // Sort each list by sortOrder
    for (const list of m.values()) {
      list.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return m;
  }, [frontDeskDefaults]);

  /** Resolve coverage for a given office + date. */
  function resolveCoverage(office: string, dateIso: string): CoverageCell {
    const overrides = overrideMap.get(`${office}|${dateIso}`) ?? [];
    if (overrides.length > 0) {
      // A "no coverage" marker on the day wins over any other override rows.
      if (overrides.some((o) => o.isNoCoverage)) {
        return { entries: [], isNoCoverage: true, isOverride: true };
      }
      const entries = overrides.map((o) => {
        const emp = empMap.get(o.staffIndex);
        return {
          employeeName: emp?.shortName ?? emp?.displayName ?? o.staffIndex,
          timeRange:
            o.timeIn && o.timeOut
              ? `${formatTime12(o.timeIn)} - ${formatTime12(o.timeOut)}`
              : '',
        };
      });
      return { entries, isNoCoverage: false, isOverride: true };
    }

    // Fall back to defaults (already sorted by sortOrder)
    const dow = isoDayOfWeek(dateIso);
    const defaults = defaultMap.get(`${office}|${dow}`) ?? [];
    if (defaults.length === 0) {
      return { entries: [], isNoCoverage: true, isOverride: false };
    }

    const entries = defaults.map((def) => {
      const emp = empMap.get(def.staffIndex);
      return {
        employeeName: emp?.shortName ?? emp?.displayName ?? def.staffIndex,
        timeRange: `${formatTime12(def.timeIn)} - ${formatTime12(def.timeOut)}`,
      };
    });
    return { entries, isNoCoverage: false, isOverride: false };
  }

  return (
    <div className="card">
      <div className="hd" style={{ fontSize: 14, fontWeight: 600 }}>
        Front Desk Coverage
      </div>
      <div className="bd" style={{ overflowX: 'auto' }}>
        {/* Day headers */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '120px repeat(5, 1fr)',
            gap: 1,
            marginBottom: 12,
          }}
        >
          <div /> {/* spacer for office column */}
          {weekDates.map((d, i) => {
            const isToday = d === today;
            return (
              <div
                key={d}
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '6px 4px',
                  borderRadius: 6,
                  color: isToday ? 'var(--accent)' : 'var(--muted)',
                  background: isToday ? 'rgba(122,162,255,.08)' : 'transparent',
                }}
              >
                {dayHeader(d, i)}
              </div>
            );
          })}
        </div>

        {/* Office rows */}
        {editTarget && canEdit && onSaveFrontDesk && onDeleteFrontDesk && (
          <CoverageEditModal
            title={`Front Desk – ${editTarget.office}`}
            dateIso={editTarget.date}
            slots={frontDesk
              .filter((f) => f.office === editTarget.office && f.coverageDate === editTarget.date && !f.isNoCoverage)
              .map((f) => ({
                id: f.id,
                staffIndex: f.staffIndex,
                timeIn: f.timeIn,
                timeOut: f.timeOut,
              }))}
            defaults={(defaultMap.get(`${editTarget.office}|${isoDayOfWeek(editTarget.date)}`) ?? []).map((d) => ({
              staffIndex: d.staffIndex,
              timeIn: d.timeIn,
              timeOut: d.timeOut,
            }))}
            allowDuplicateEmployees
            employees={employees}
            onAdd={(form) =>
              onSaveFrontDesk({
                office: editTarget.office,
                coverageDate: editTarget.date,
                staffIndex: form.staffIndex,
                timeIn: form.timeIn,
                timeOut: form.timeOut,
                note: null,
              })
            }
            onEdit={(id, form) =>
              onSaveFrontDesk({
                id,
                office: editTarget.office,
                coverageDate: editTarget.date,
                staffIndex: form.staffIndex,
                timeIn: form.timeIn,
                timeOut: form.timeOut,
                note: null,
              })
            }
            onDelete={onDeleteFrontDesk}
            onClose={() => setEditTarget(null)}
          />
        )}
        {FRONT_DESK_OFFICES.map((office) => {
          const officeColor = getOfficeColor(office);
          return (
            <div key={office} style={{ marginBottom: 14 }}>
              {/* Office header */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '120px repeat(5, 1fr)',
                  gap: 1,
                  alignItems: 'stretch',
                }}
              >
                <div style={{ padding: '4px 0', display: 'flex', alignItems: 'center' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 10px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 700,
                      background: `${officeColor}33`,
                      color: officeColor,
                    }}
                  >
                    {office}
                  </span>
                </div>

                {/* Day cells */}
                {weekDates.map((dateIso) => {
                  const cell = resolveCoverage(office, dateIso);
                  const isToday = dateIso === today;

                  return (
                    <div
                      key={dateIso}
                      style={{
                        position: 'relative',
                        textAlign: 'center',
                        padding: '8px 6px',
                        borderRadius: 8,
                        background: isToday
                          ? 'rgba(122,162,255,.06)'
                          : 'rgba(255,255,255,.02)',
                        border: isToday
                          ? '1px solid rgba(122,162,255,.15)'
                          : '1px solid transparent',
                        minHeight: 54,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                      }}
                    >
                      {canEdit && (
                        <button
                          title="Edit coverage"
                          onClick={() => setEditTarget({ office, date: dateIso })}
                          style={{
                            all: 'unset', cursor: 'pointer',
                            position: 'absolute', top: 4, right: 4,
                            padding: 3, borderRadius: 4,
                            color: 'var(--muted)',
                          }}
                        >
                          <Pencil size={10} />
                        </button>
                      )}
                      {cell.isNoCoverage ? (
                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--bad)' }}>
                          No Coverage
                        </div>
                      ) : (
                        cell.entries.map((entry, i) => (
                          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                              {entry.employeeName}
                            </div>
                            {entry.timeRange && (
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>
                                {entry.timeRange}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                      {cell.isOverride && !cell.isNoCoverage && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: '1px 6px',
                            borderRadius: 6,
                            background: 'rgba(255,209,102,.15)',
                            color: 'var(--warn)',
                            marginTop: 2,
                          }}
                        >
                          Custom
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FrontDeskCoverage;
